import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { AgGridReact } from "ag-grid-react";
import { isEmpty, isEqual, get } from "lodash";
import { Header, Button } from "semantic-ui-react";
import downloadFile from "js-file-download";
import { toast } from "react-semantic-toasts";

import {
    loadStampGridPage,
    downloadAsCSV,
    changeStampGridPage,
    changeStampGridState,
} from "./actions";
import StampRollContainerPicker from "../roll/containers/picker";
import GridPageSizePicker from "../../common/components/grid-page-size-picker";
import { isCompanyFeatureActive } from "utilities/dist/company/helpers";
import { CompanyFeatures } from "utilities/dist/company/constants";

const NoStampsOverlay = () => {
    return (
        <div className="tm-20">
            <Header>No stamp history found.</Header>
        </div>
    );
};

class DataGrid extends React.Component {
    state = {
        params: null,
        isExporting: false,
    };
    grid = null;
    gridDataSource = {
        getRows: params => {
            this.setState({
                params,
            });
            this.props.loadStampGridPage(this.props, params);
        },
    };

    componentWillUnmount() {
        this.grid = null;
    }

    onGridReady = grid => {
        this.grid = grid;

        setTimeout(() => this.grid && this.grid.api.sizeColumnsToFit(), 500);

        this.grid.api.setDatasource(this.gridDataSource);
        this.grid.columnApi.applyColumnState({ state: this.props.sortModel, defaultState: {} });
        this.grid.api.setFilterModel(this.props.filterModel);

        this.grid.api.addEventListener("sortChanged", () => {
            const colStates = this.grid.columnApi.getColumnState();
            const sortModel = colStates
                .filter(colState => colState.sort)
                .map(colState => ({
                    colId: colState.colId,
                    sort: colState.sort,
                }));

            this.props.changeStampGridState({
                sortModel,
            });
        });

        this.grid.api.addEventListener("filterChanged", () => {
            this.props.changeStampGridState({
                filterModel: this.grid.api.getFilterModel(),
            });
        });
    };

    reloadGridData = () => {
        this.grid && this.grid.api.refreshInfiniteCache();
    };

    changePage = () => {
        if (this.grid) {
            const page = this.grid.api.paginationGetCurrentPage() + 1;

            if (page === this.props.currentPage) return;

            this.props.changeStampGridPage(page);
        }
    };

    componentDidUpdate(prevProps) {
        if (!this.grid) return;

        if (prevProps.isLoading && !this.props.isLoading) {
            this.grid.api.hideOverlay();
        }

        if (!prevProps.isLoading && this.props.isLoading) {
            this.grid.api.showLoadingOverlay();
        }

        if (!this.props.isLoading && this.props.stamps.length < 1) {
            this.grid.api.showNoRowsOverlay();
        }

        if (!isEqual(prevProps.filterModel, this.props.filterModel)) {
            this.grid.api.setFilterModel(this.props.filterModel);
        }
    }

    downloadCSV = async () => {
        this.setState({
            isExporting: true,
        });
        try {
            const data = await downloadAsCSV(this.props, this.state.params);
            downloadFile(data, `tabc_stamps_${Date.now()}.csv`);
        } catch (ex) {
            toast({
                icon: "file",
                type: "error",
                title: `Download CSV`,
                description: `Error occurred while generating CSV`,
            });
        }
        this.setState({
            isExporting: false,
        });
    };

    render() {
        const { currentStampRoll, pageSize, filterModel, company } = this.props;

        const isCustomerInvoiceNumberingActive = isCompanyFeatureActive(
            company,
            CompanyFeatures.CUSTOMER_INVOICE_NUMBERING
        );

        let columnDefs = [...this.props.columnDefs];
        if (!isCustomerInvoiceNumberingActive) {
            columnDefs = columnDefs.map(def => {
                if (def.field === "number") {
                    def.type = "numericColumn";
                    def.filter = "agNumberColumnFilter";
                }

                return def;
            });
        }

        return (
            <>
                <div className="clear bm-10">
                    {!isEmpty(currentStampRoll) && (
                        <p className="fl">
                            Received stamps {currentStampRoll.received_at} |{" "}
                            <b>{currentStampRoll.to - currentStampRoll.from + 1}</b> Stamps{" "}
                            <b>
                                {currentStampRoll.prefix || ""}
                                {currentStampRoll.from} - {currentStampRoll.prefix || ""}
                                {currentStampRoll.to}
                            </b>
                        </p>
                    )}

                    <GridPageSizePicker
                        button
                        compact
                        pointing="top"
                        pageSize={pageSize}
                        text={`${pageSize}/page`}
                        className="right floated basic"
                        onChange={this.props.changeStampGridState}
                    />

                    <StampRollContainerPicker
                        button
                        compact
                        clearable
                        pointing="top"
                        className="right floated basic"
                        value={get(filterModel, "roll_id.filter")}
                        onChange={(e, data) => {
                            const filterModel = this.grid.api.getFilterInstance("roll_id");
                            filterModel.setModel(
                                data.value
                                    ? {
                                          type: "equals",
                                          filter: data.value,
                                      }
                                    : null
                            );
                            filterModel.applyModel();
                            this.grid.api.onFilterChanged();
                        }}
                    />

                    <Button
                        size="mini"
                        icon="download"
                        floated="right"
                        content="Export"
                        onClick={this.downloadCSV}
                        loading={this.state.isExporting}
                        disabled={this.state.isExporting}
                    />
                </div>
                <div className="ag-theme-balham bm-10">
                    <AgGridReact
                        pagination
                        paginationPageSize={this.props.pageSize}
                        rowHeight={56}
                        rowSelection="single"
                        domLayout="autoHeight"
                        rowModelType="infinite"
                        onGridReady={this.onGridReady}
                        columnDefs={columnDefs}
                        cacheBlockSize={this.props.pageSize}
                        onPaginationChanged={this.changePage}
                        noRowsOverlayComponent={NoStampsOverlay}
                        defaultColDef={{
                            resizable: true,
                            sortable: true,
                            floatingFilter: true,
                            autoHeight: true,
                        }}
                    />
                </div>
            </>
        );
    }
}

const mapStateToProps = ({ stampGrid, stampWarning, auth }) => ({
    stamps: stampGrid.stamps,
    rollId: stampGrid.rollId,
    pageSize: stampGrid.pageSize,
    isLoading: stampGrid.isLoading,
    sortModel: stampGrid.sortModel,
    columnDefs: stampGrid.columnDefs,
    filterModel: stampGrid.filterModel,
    currentPage: stampGrid.currentPage,

    company: auth.user.company,
    currentStampRoll: stampWarning.data.currentRoll,
});

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            loadStampGridPage,
            changeStampGridState,
            changeStampGridPage: page => dispatch(changeStampGridPage(page)),
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(DataGrid);
